import Swiper from 'swiper/bundle'
import 'swiper/css/bundle'

const MARQUEE_PX_PER_MS = 0.05

const waitForImages = (root) => {
  const pending = Array.from(root.querySelectorAll('img')).filter(img => !img.complete)
  if (!pending.length) return Promise.resolve()
  return Promise.all(pending.map(img => new Promise(resolve => {
    img.addEventListener('load', resolve, { once: true })
    img.addEventListener('error', resolve, { once: true })
  })))
}

// Continuous marquee (e.g. an autoplaying logo strip): Swiper's slide-by-slide
// engine isn't built for this. Chaining slideNext() calls per slide - the
// previous approach - drifts in speed (step duration is derived from each
// slide's measured width, which can differ by a subpixel from its rendered
// width) and can jump when Swiper's loop mode silently repositions the track.
// Driving a duplicated track with a single linear Web Animations API loop
// avoids both: one constant-speed animation, no per-step rescheduling.
const initMarquee = (slider, swiperElement, spaceBetween, spaceBetweenMobile) => {
  const wrapper = swiperElement.querySelector('.swiper-wrapper')
  const originalSlides = Array.from(wrapper.children)
  if (!originalSlides.length) return

  // The track loops endlessly, so there's never a real beginning/end - keep
  // both edge fades shown permanently instead of leaving them unset.
  slider.dataset.beginning = 'false'
  slider.dataset.end = 'false'

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  let animation = null
  let animationDuration = 0
  let rebuildTimeout = null

  const appendClone = (slide) => {
    const clone = slide.cloneNode(true)
    clone.setAttribute('aria-hidden', 'true')
    wrapper.appendChild(clone)
  }

  const build = async () => {
    animation?.cancel()
    animation = null

    wrapper.innerHTML = ''
    originalSlides.forEach(slide => wrapper.appendChild(slide.cloneNode(true)))
    wrapper.style.gap = (window.innerWidth < 1024 ? spaceBetweenMobile : spaceBetween) + 'px'

    await waitForImages(wrapper)

    if (reduceMotion) {
      // No looping/animation for reduced motion - but the row can still be
      // wider than the container, so it needs a way to reach the rest of the
      // logos. Swiper isn't driving this element, so fall back to native
      // scrolling rather than leaving the overflow permanently clipped.
      swiperElement.style.overflowX = 'auto'
      return
    }

    originalSlides.forEach(appendClone)

    // Distance from the start of the first set to the start of its clone -
    // exactly the offset a seamless loop needs to shift by, measured
    // directly instead of recomputed from gap/width math.
    const distance = wrapper.children[originalSlides.length].offsetLeft
    if (!distance) return

    const containerWidth = swiperElement.clientWidth
    while (wrapper.scrollWidth < containerWidth + distance) {
      originalSlides.forEach(appendClone)
    }

    wrapper.style.willChange = 'transform'
    animationDuration = distance / MARQUEE_PX_PER_MS
    animation = wrapper.animate(
      [{ transform: 'translateX(0)' }, { transform: `translateX(-${distance}px)` }],
      { duration: animationDuration, iterations: Infinity, easing: 'linear' }
    )
  }

  build()

  window.addEventListener('resize', () => {
    clearTimeout(rebuildTimeout)
    rebuildTimeout = setTimeout(build, 200)
  })

  // Reduced motion falls back to plain native scrolling (see build()) -
  // dragging it ourselves would mean calling preventDefault() on the very
  // touch gesture the browser needs to scroll it.
  if (reduceMotion) return

  // Manual drag: pause the loop and scrub its currentTime to follow the
  // pointer instead of touching transform directly, so resuming afterwards
  // continues the same infinite animation with no re-sync or jump.
  wrapper.addEventListener('dragstart', e => e.preventDefault())
  swiperElement.style.touchAction = 'pan-y'

  let pointerId = null
  let dragging = false
  let dragStartX = 0
  let dragStartY = 0
  let dragStartTime = 0
  let dragMoved = false

  const onPointerMove = (e) => {
    if (e.pointerId !== pointerId) return
    const deltaX = e.clientX - dragStartX
    const deltaY = e.clientY - dragStartY

    if (!dragging) {
      if (Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5) return
      if (Math.abs(deltaX) <= Math.abs(deltaY)) return // vertical scroll intent
      dragging = true
      dragMoved = true
      animation?.pause()
      wrapper.style.cursor = 'grabbing'
      wrapper.style.userSelect = 'none'
    }

    e.preventDefault()
    if (!animation) return

    let time = dragStartTime - deltaX / MARQUEE_PX_PER_MS
    if (animationDuration > 0) {
      time = ((time % animationDuration) + animationDuration) % animationDuration
    }
    animation.currentTime = time
  }

  const endDrag = (e) => {
    if (e.pointerId !== pointerId) return
    if (dragging) {
      animation?.play()
      wrapper.style.cursor = ''
      wrapper.style.userSelect = ''
    }
    pointerId = null
    dragging = false
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', endDrag)
    window.removeEventListener('pointercancel', endDrag)
  }

  wrapper.addEventListener('pointerdown', (e) => {
    if (e.button !== undefined && e.button !== 0) return
    pointerId = e.pointerId
    dragStartX = e.clientX
    dragStartY = e.clientY
    dragStartTime = animation?.currentTime || 0
    dragMoved = false
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', endDrag)
    window.addEventListener('pointercancel', endDrag)
  })

  // Same directional cursor as the other sliders while hovering (a
  // "grabbing" hand takes over on the wrapper - and wins, being the closer
  // ancestor - only for the duration of an actual drag).
  slider.addEventListener('mousemove', (e) => {
    const rect = slider.getBoundingClientRect()
    const x = e.clientX - rect.left

    if (e.target.closest('button, a, .swiper-pagination, .swiper-scrollbar')) {
      slider.style.cursor = 'auto'
      slider.dataset.cursorDirection = ''
      return
    }

    if (x < rect.width / 2) {
      slider.style.cursor = 'var(--cursor-prev)'
      slider.dataset.cursorDirection = 'prev'
    } else {
      slider.style.cursor = 'var(--cursor-next)'
      slider.dataset.cursorDirection = 'next'
    }
  })

  // Suppress the click a dragged link would otherwise fire on release.
  wrapper.addEventListener('click', (e) => {
    if (!dragMoved) return
    dragMoved = false
    e.preventDefault()
    e.stopPropagation()
  }, true)
}

export const initializeSlider = () => {
  const sliders = document.querySelectorAll('[data-component="slider"]')

  sliders.forEach(slider => {
    const swiperElement = slider.querySelector('.swiper')
    const slidesPerViewAttr = swiperElement.dataset.slidesPerView
    const isAutoWidth = slidesPerViewAttr === 'auto'
    const slidesPerView = isAutoWidth ? 'auto' : parseFloat(slidesPerViewAttr) || 1
    const autoplay = swiperElement.dataset.autoplay === 'true'
    const marquee = swiperElement.dataset.marquee === 'true'
    const gapAttr = swiperElement.dataset.gap
    const spaceBetween = gapAttr === 'true' ? 32 : gapAttr === 'false' ? 0 : parseFloat(gapAttr) || 0
    const gapMobileAttr = swiperElement.dataset.gapMobile
    const spaceBetweenMobile = gapMobileAttr ? parseFloat(gapMobileAttr) || 0 : spaceBetween

    if (marquee && autoplay) {
      initMarquee(slider, swiperElement, spaceBetween, spaceBetweenMobile)
      return
    }

    const options = {
      slidesPerView: slidesPerView,
      spaceBetween: spaceBetween,
      loop: false,
      autoplay: autoplay ? { delay: 5000 } : false,
      simulateTouch: true,
      pagination: {
        el: slider.querySelector('.swiper-pagination'),
        clickable: true
      },
      navigation: {
        nextEl: slider.querySelector('.button-next'),
        prevEl: slider.querySelector('.button-prev')
      },
      scrollbar: {
        el: slider.querySelector('.swiper-scrollbar')
      },
      breakpoints: {
        320: {
          slidesPerView: isAutoWidth ? 'auto' : swiperElement.dataset.slidesPerViewMobile || 1,
          spaceBetween: spaceBetweenMobile
        },
        640: {
          slidesPerView: isAutoWidth ? 'auto' : slidesPerView > 2 ? 2 : 1,
          spaceBetween: spaceBetweenMobile
        },
        1024: {
          slidesPerView: slidesPerView,
          spaceBetween: spaceBetween
        }
      }
    }

    const swiperInstance = new Swiper(swiperElement, options)
    swiperElement.swiperInstance = swiperInstance

    // Update data attributes for gradients and navigation state
    const updateNavigationState = () => {
      slider.dataset.beginning = swiperInstance.isBeginning
      slider.dataset.end = swiperInstance.isEnd
    }

    swiperInstance.on('slideChange', updateNavigationState)
    updateNavigationState() // Initial check

    // Cursor and Click Navigation
    slider.addEventListener('mousemove', (e) => {
      const rect = slider.getBoundingClientRect()
      const x = e.clientX - rect.left

      // Reset cursor for interactive elements like buttons and pagination
      if (e.target.closest('button, a, .swiper-pagination, .swiper-scrollbar')) {
        slider.style.cursor = 'auto'
        slider.dataset.cursorDirection = '' // Clear direction when on links
        return
      }

      if (x < rect.width / 2) {
        slider.style.cursor = 'var(--cursor-prev)'
        slider.dataset.cursorDirection = 'prev'
      } else {
        slider.style.cursor = 'var(--cursor-next)'
        slider.dataset.cursorDirection = 'next'
      }
    })

    slider.addEventListener('click', (e) => {
      // Prevent sliding if a link or button was clicked
      if (e.target.closest('a, button, .swiper-pagination, .swiper-scrollbar')) return

      if (slider.dataset.cursorDirection === 'prev') {
        swiperInstance.slidePrev()
      } else {
        swiperInstance.slideNext()
      }
    })

    // Set equal height for all slides
    const setEqualSlideHeights = () => {
      if (swiperElement.dataset.sameHeight === 'false') return
      
      const slides = swiperElement.querySelectorAll('.swiper-slide')
      let maxHeight = 0

      slides.forEach(slide => {
        slide.style.height = 'auto'
        if (slide.offsetHeight > maxHeight) maxHeight = slide.offsetHeight
      })

      if (maxHeight === 0) return

      slides.forEach(slide => {
        slide.style.height = maxHeight + 'px'
      })
    }

    // Initial height adjustment
    setEqualSlideHeights()

    // Adjust on window resize
    window.addEventListener('resize', setEqualSlideHeights)

    // Re-adjust heights after 0.5 seconds to account for any late content loading
    setTimeout(setEqualSlideHeights, 500)

    // Images have no intrinsic size until loaded, which can leave
    // slidesPerView:'auto' slides measured at 0 width. Recalculate once each
    // image has actually loaded instead of relying on a fixed timeout.
    swiperElement.querySelectorAll('img').forEach(img => {
      if (img.complete) return
      img.addEventListener('load', () => {
        swiperInstance.update()
        setEqualSlideHeights()
      })
    })
  })
}
